type UiRouterState = typeof Tools.$state & {
	// ui-router's internal state object; three callers read `$current.self.name`. Not in Tools.d.ts.
	$current: any;
};

/*
	Standalone replacement for the Tools.$state global (WEB-14054), the same play as EventBus (WEB-7608).

	This is a facade, not a reimplementation. While Angular is alive ui-router owns navigation: the
	react-root-* compat states, the $stateChangeStart guards and every Angular state all go through the
	one $state service. So every member delegates straight to the injected $state and returns whatever
	ui-router returns — the transition promise from go(), its option handling, its own params object.
	Swapping ui-router out later is then a change to this one file instead of 370 call sites.

	The transport is injected from upsales/common/services/tools.js at the exact line that installs
	Tools.$state, so the facade is available from precisely the same moment the global was. Before that
	the global was undefined and callers threw a TypeError; the facade throws a clearer error instead
	of silently dropping a navigation.
*/
class StateRouter {
	private state: UiRouterState | null = null;

	setState(state: UiRouterState | null) {
		this.state = state;
	}

	private get transport(): UiRouterState {
		if (!this.state) {
			throw new Error('StateRouter was used before the Angular router was injected');
		}
		return this.state;
	}

	go = (...args: Parameters<UiRouterState['go']>): ReturnType<UiRouterState['go']> => this.transport.go(...args);

	href = (...args: Parameters<UiRouterState['href']>): ReturnType<UiRouterState['href']> =>
		this.transport.href(...args);

	get = (...args: Parameters<UiRouterState['get']>): ReturnType<UiRouterState['get']> => this.transport.get(...args);

	reload = (): ReturnType<UiRouterState['reload']> => this.transport.reload();

	// Read through on every access: ui-router replaces these objects on each transition.
	get params(): UiRouterState['params'] {
		return this.transport.params;
	}

	get current(): UiRouterState['current'] {
		return this.transport.current;
	}

	get $current(): UiRouterState['$current'] {
		return this.transport.$current;
	}
}

export default new StateRouter();
